import {
  Building2,
  Database,
  ExternalLink,
  Search,
} from "lucide-react";
import { useState } from "react";
import {
  CopyableDate,
  CopyableText,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableValue,
  FeedbackBanner,
  PageHeader,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";

function looksLikeTaxId(value) {
  return /^[\d\s-]+$/.test(value) && value.replace(/\D/g, "").length === 11;
}

function formatSourceDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
  }).format(date);
}

export function TaxIdentitySearchPage() {
  const api = useApi();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [state, setState] = useState({
    loading: false,
    searched: false,
    error: "",
  });

  const search = async (event) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 3 || state.loading) return;

    setState({ loading: true, searched: false, error: "" });
    try {
      const data = looksLikeTaxId(normalizedQuery)
        ? [await api.getTaxIdentity(normalizedQuery)]
        : await api.searchTaxIdentities(normalizedQuery, 20);
      setResults(data);
      setState({ loading: false, searched: true, error: "" });
    } catch (error) {
      setResults([]);
      setState({
        loading: false,
        searched: true,
        error: error.message,
      });
    }
  };

  return (
    <div className="page tax-search-page instrumenta-directory-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Consulta fiscal"
        title="Buscá por CUIT o razón social."
        description="Ingresá el CUIT para identificar una sociedad, o escribí su nombre para encontrar coincidencias en el directorio oficial."
      />

      <form className="tax-search-form" onSubmit={search}>
        <label htmlFor="tax-identity-query">
          CUIT o razón social
          <span className="tax-search-control">
            <Search size={20} />
            <input
              id="tax-identity-query"
              data-justiniano-target="tax-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ej. 30-71733544-5 o E.N.S. 21 S.A.S."
              autoComplete="off"
              autoFocus
            />
            <button
              className="primary-button"
              disabled={query.trim().length < 3 || state.loading}
            >
              {state.loading ? "Buscando…" : "Buscar"}
            </button>
          </span>
        </label>
        <p>
          Podés ingresar el CUIT con o sin guiones. Para buscar por nombre,
          escribí al menos tres caracteres.
        </p>
      </form>

      {state.error && <FeedbackBanner>{state.error}</FeedbackBanner>}

      <section className="tax-results" aria-live="polite">
        {results.length > 0 && (
          <div className="tax-results-heading">
            <div>
              <span className="eyebrow">Resultados</span>
              <h2>
                {results.length} coincidencia{results.length === 1 ? "" : "s"}
              </h2>
            </div>
            <Database size={20} />
          </div>
        )}

        {results.length > 0 && (
          <DataTable
            ariaLabel="Identidades fiscales encontradas"
            className="directory-results-table"
            density="compact"
            minWidth="700px"
          >
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell className="directory-table-primary">
                  Razón social
                </DataTableHeaderCell>
                <DataTableHeaderCell>CUIT</DataTableHeaderCell>
                <DataTableHeaderCell>Fuente</DataTableHeaderCell>
                <DataTableHeaderCell align="end" className="directory-table-action">
                  Consulta
                </DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {results.map((identity) => {
                const sourceDate = formatSourceDate(identity.source?.updatedAt);
                return (
                  <DataTableRow key={identity.id}>
                    <DataTableCell>
                      <DataTablePrimaryCell
                        icon={<Building2 size={16} />}
                        title={identity.legalName}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <DataTableValue
                        primary={(
                          <CopyableText
                            copiedMessage="CUIT copiado"
                            label="CUIT"
                            value={identity.taxId}
                          />
                        )}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <DataTableValue
                        primary={identity.source?.label || "Directorio fiscal"}
                        secondary={sourceDate ? (
                          <CopyableDate value={identity.source?.updatedAt}>
                            Actualizada {sourceDate}
                          </CopyableDate>
                        ) : null}
                      />
                    </DataTableCell>
                    <DataTableCell align="end">
                      {identity.source?.url ? (
                        <a
                          className="directory-table-link"
                          href={identity.source.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Ver fuente
                          <ExternalLink size={14} />
                        </a>
                      ) : "—"}
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        )}

        {state.searched && !state.error && !results.length && (
          <div className="tax-empty-state">
            <Search size={25} />
            <h2>No encontramos coincidencias</h2>
            <p>
              Revisá la escritura del nombre o probá con menos palabras.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
